import { Box, Checkbox, CircularProgress, FormControlLabel, Popover } from "@mui/material";
import { useCallback, useContext, useMemo, useRef, useState } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import FilterBar from "../../components/FilterBar/FilterBar";
import SavedSearchBar from "../../components/SavedSearch/SavedSearchBar";
import { SearchFilters } from "../../components/FilterBar/filterChipDefs";
import DetailPane from "../../components/DetailPane/DetailPane";
import type { TabId } from "../../components/DetailPane/DetailTabs";
import Pagination from "../../components/common/Pagination";
import BulkEditDrawer from "../../components/BulkEdit/BulkEditDrawer";
import EditInteractionDrawer from "../../components/EditInteraction/EditInteractionDrawer";
import BulkExportPopover from "../../components/BulkExport/BulkExportPopover";
import { useSearch } from "../../../hooks/useSearch";
import { useTranscriptDownload } from "../../../hooks/useTranscription";
import { downloadInteractionFile } from "../../../helpers/fileDownload";
import { GetInteractionsRequest } from "../../../api/interaction";
import Interaction, { InteractionType } from "../../../types/interaction/Interaction";
import { COLUMN_DEFS, ColumnRenderContext } from "./columnDefs";
import TableRow from "./TableRow";
import { useV2HeaderNames } from "../../hooks/useV2HeaderNames";
import { useSites } from "../../../hooks/useSite";
import { AuthContext } from "../../../context/AuthContext";
import { paramsToFilters, filtersToParams } from "./filterParams";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const COLUMN_ORDER_STORAGE_KEY = "v2-search-column-order";
const COLUMN_VISIBILITY_STORAGE_KEY = "v2-search-column-visibility";
const DENSITY_STORAGE_KEY = "v2-search-density";
const COLUMN_WIDTHS_STORAGE_KEY = "v2-search-column-widths";

type Density = "compact" | "standard" | "comfortable";

const DENSITY_STYLES: Record<Density, { fontSize: string; headerFontSize: string; cellPx: string; cellPy: string }> = {
  compact:     { fontSize: "0.75rem",   headerFontSize: "0.68rem", cellPx: "8px",  cellPy: "4px" },
  standard:    { fontSize: "0.8125rem", headerFontSize: "0.72rem", cellPx: "10px", cellPy: "7px" },
  comfortable: { fontSize: "0.875rem",  headerFontSize: "0.75rem", cellPx: "12px", cellPy: "10px" },
};

const DEFAULT_HIDDEN_COLUMNS = new Set([
  "callId",
  "masterInteractionId",
  "participants",
  "segmentIds",
  "skill",
]);

const DEFAULT_SORT: { field: string; sort: "asc" | "desc" }[] = [
  { field: "startTime", sort: "desc" },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface V2SearchProps {
  viewId?: string;
}

export default function V2Search({ viewId }: V2SearchProps = {}) { // NOSONAR
  const { t } = useTranslation(["search", "common", "grid"]);
  const { customerId } = useParams<{ customerId: string }>();
  const { user } = useContext(AuthContext);

  // -- Custom header names from API -----------------------------------------
  const { getLabel } = useV2HeaderNames(customerId);

  // -- Selection & detail ---------------------------------------------------
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
  const [viewingId, setViewingId] = useState<string | null>(null);
  const [initialTab, setInitialTab] = useState<TabId>("transcript");

  // -- Bulk edit drawer -----------------------------------------------------
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const canBulkEdit = user?.hasPermission("interactions:Update") ?? false;

  // -- Single edit drawer ---------------------------------------------------
  const [editingInteraction, setEditingInteraction] = useState<Interaction | null>(null);

  // -- Bulk export popover ---------------------------------------------------
  const [exportAnchor, setExportAnchor] = useState<HTMLElement | null>(null);

  // -- Pagination -----------------------------------------------------------
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(1000);

  // -- Sorting --------------------------------------------------------------
  const [sortModel, setSortModel] = useState<{ field: string; sort: "asc" | "desc" }[]>(
    DEFAULT_SORT,
  );

  // -- Filter (synced to URL query string) -----------------------------------
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<SearchFilters>(() => paramsToFilters(searchParams));

  // -- Density (persisted to localStorage) ------------------------------------
  const [density, setDensity] = useState<Density>(() => {
    const saved = localStorage.getItem(DENSITY_STORAGE_KEY);
    if (saved === "compact" || saved === "standard" || saved === "comfortable") return saved;
    return "compact";
  });
  const [densityMenuAnchor, setDensityMenuAnchor] = useState<null | HTMLElement>(null);

  const handleDensityChange = (d: Density) => {
    setDensity(d);
    localStorage.setItem(DENSITY_STORAGE_KEY, d);
    setDensityMenuAnchor(null);
  };

  const ds = DENSITY_STYLES[density];

  const estimatedRowHeightStandardOrComfortable = density === "standard" ? 36 : 44;
  const estimatedRowHeight = density === "compact" ? 28 : estimatedRowHeightStandardOrComfortable;

  // -- Column ordering (persisted to localStorage) --------------------------
  const [columnOrder, setColumnOrder] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem(COLUMN_ORDER_STORAGE_KEY);
      if (saved) {
        const parsed: string[] = JSON.parse(saved);
        // Validate: only keep fields that still exist in COLUMN_DEFS
        const validFields = new Set(COLUMN_DEFS.map((c) => c.field));
        const filtered = parsed.filter((f) => validFields.has(f));
        // Append any new columns that weren't in the saved order
        const savedSet = new Set(filtered);
        for (const def of COLUMN_DEFS) {
          if (!savedSet.has(def.field)) filtered.push(def.field);
        }
        return filtered;
      }
    } catch {
      // fall through
    }
    return COLUMN_DEFS.map((c) => c.field);
  });

  // -- Column visibility (persisted to localStorage) ------------------------
  const [hiddenColumns, setHiddenColumns] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem(COLUMN_VISIBILITY_STORAGE_KEY);
      if (saved) return new Set(JSON.parse(saved));
    } catch {
      // fall through
    }
    return new Set(DEFAULT_HIDDEN_COLUMNS);
  });

  const [columnsMenuAnchor, setColumnsMenuAnchor] = useState<null | HTMLElement>(null);
  const [columnSearch, setColumnSearch] = useState("");

  const toggleColumnVisibility = (field: string) => {
    setHiddenColumns((prev) => {
      const next = new Set(prev);
      if (next.has(field)) next.delete(field);
      else next.add(field);
      try {
        localStorage.setItem(COLUMN_VISIBILITY_STORAGE_KEY, JSON.stringify([...next]));
      } catch { /* ignore */ }
      return next;
    });
  };

  // -- Scroll container ref for virtualizer ---------------------------------
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLTableElement>(null);

  // -- Drag state -----------------------------------------------------------
  const dragIndexRef = useRef<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  // -- Column widths (persisted to localStorage) ----------------------------
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() => {
    try {
      const saved = localStorage.getItem(COLUMN_WIDTHS_STORAGE_KEY);
      if (saved) return JSON.parse(saved);
    } catch { /* fall through */ }
    return {};
  });

  const columnWidthVars = useMemo(() => {
    const vars: Record<string, string> = {};
    for (const [field, w] of Object.entries(columnWidths)) {
      vars[`--col-w-${field}`] = `${w}px`;
    }
    return vars;
  }, [columnWidths]);

  const handleResizeStart = useCallback((e: React.MouseEvent, field: string) => {
    e.preventDefault();
    e.stopPropagation();
    const th = (e.target as HTMLElement).closest("th");
    if (!th) return;
    const startWidth = th.getBoundingClientRect().width;
    const startX = e.clientX;
    let currentWidth = startWidth;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      currentWidth = Math.max(50, startWidth + (moveEvent.clientX - startX));
      // Direct DOM update — no React re-render
      tableRef.current?.style.setProperty(`--col-w-${field}`, `${currentWidth}px`);
    };

    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      // Commit final width to React state + localStorage
      setColumnWidths(prev => {
        const next = { ...prev, [field]: currentWidth };
        try { localStorage.setItem(COLUMN_WIDTHS_STORAGE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
        return next;
      });
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  }, []);

  // -- Build column list in current order -----------------------------------
  const orderedColumns = useMemo(() => {
    const columnDefsMap = new Map(COLUMN_DEFS.map((c) => [c.field, c]));
    return columnOrder
      .map((field) => columnDefsMap.get(field))
      .filter(Boolean) as typeof COLUMN_DEFS;
  }, [columnOrder]);

  // Type column is pinned — exclude from scrollable data columns
  const visibleColumns = useMemo(
    () => orderedColumns.filter((c) => !hiddenColumns.has(c.field) && c.field !== "interactionType"),
    [orderedColumns, hiddenColumns],
  );

  const typeColumnDef = useMemo(
    () => COLUMN_DEFS.find((c) => c.field === "interactionType"),
    [],
  );

  // -- Data -----------------------------------------------------------------
  const request: GetInteractionsRequest = {
    pageIndex: page,
    pageSize: pageSize,
    sortBehavior: sortModel,
    agentName: filters.agentName || undefined,
    agentNameExactMatch: filters.agentNameExactMatch || undefined,
    agentId: filters.agentId || undefined,
    from: filters.from || undefined,
    to: filters.to || undefined,
    siteIds: filters.siteIds?.length ? filters.siteIds : undefined,
    interactionType: filters.interactionType as InteractionType | undefined,
    tags: filters.tags?.length ? filters.tags : undefined,
    direction: filters.direction || undefined,
    phoneNumber: filters.phoneNumber || undefined,
    dnis: filters.dnis || undefined,
    extension: filters.extension || undefined,
    device: filters.device || undefined,
    group: filters.group || undefined,
    skill: filters.skill || undefined,
    legalHold: filters.legalHold ?? undefined,
    hasMedia: filters.hasMedia ?? undefined,
    hasRetention: filters.hasRetention ?? undefined,
    fromRetention: filters.fromRetention || undefined,
    toRetention: filters.toRetention || undefined,
    minDuration: filters.minDuration ? filters.minDuration * 1000 : undefined,
    maxDuration: filters.maxDuration ? filters.maxDuration * 1000 : undefined,
    sourceId: filters.sourceId || undefined,
    interactionId: filters.interactionId || undefined,
    masterInteractionId: filters.masterInteractionId || undefined,
    segmentId: filters.segmentId || undefined,
    callId: filters.callId || undefined,
    hasTranscription: filters.hasTranscription ?? undefined,
  };

  const { data, isLoading } = useSearch(customerId, viewId, request);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const interactions = data?.interactions ?? [];
  const totalCount = data?.totalCount ?? 0;
  const totalPages = data?.totalPages ?? 0;

  // -- Row virtualizer -------------------------------------------------------
  const rowVirtualizer = useVirtualizer({
    count: interactions.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => estimatedRowHeight,
    overscan: 20,
  });

  // -- Sites lookup (siteId → name) -----------------------------------------
  const { data: sitesData } = useSites(customerId);
  const siteMap = useMemo(() => {
    const m = new Map<string, string>();
    if (sitesData?.sites) {
      for (const s of sitesData.sites) {
        m.set(s.siteId, s.name);
      }
    }
    return m;
  }, [sitesData]);

  const columnCtx = useMemo<ColumnRenderContext>(() => ({ siteMap, t }), [siteMap, t]);

  const viewingInteraction = viewingId
    ? interactions.find((i) => i.interactionId === viewingId) ?? null
    : null;

  // -- Handlers: sort -------------------------------------------------------
  const handleSort = useCallback(
    (field: string) => {
      setSortModel((prev) => {
        const current = prev.find((s) => s.field === field);
        if (!current) {
          // New field: start with desc
          return [{ field, sort: "desc" }];
        }
        if (current.sort === "desc") {
          // Toggle to asc
          return [{ field, sort: "asc" }];
        }
        // Was asc: revert to default
        return DEFAULT_SORT;
      });
      setPage(0);
    },
    [],
  );

  // -- Handlers: actions -----------------------------------------------------
  const canExport = user?.hasPermission("interactions:Export") ?? false;
  const canReplay = user?.hasPermission("interactions:Replay") ?? user?.hasPermission("interactions:Read") ?? false;

  const handlePlay = useCallback((id: string) => {
    setInitialTab("transcript");
    setViewingId((prev) => (prev === id ? null : id));
  }, []);

  const handleInfo = useCallback((id: string) => {
    setInitialTab("metadata");
    setViewingId((prev) => (prev === id ? null : id));
  }, []);

  const handleEdit = useCallback((id: string) => {
    const interaction = interactions.find((i) => i.interactionId === id);
    if (interaction) {
      setEditingInteraction(interaction);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interactions]);

  const handleDownloadFile = useCallback(async (interactionId: string) => {
    if (!customerId) return;
    try {
      await downloadInteractionFile({ customerId, interactionId });
    } catch (err) {
      console.error("Failed to download file:", err);
    }
  }, [customerId]);

  const transcriptDownload = useTranscriptDownload();
  const handleDownloadTranscript = useCallback(async (interactionId: string) => {
    if (!customerId) return;
    try {
      await transcriptDownload.mutateAsync({ customerId, interactionId });
    } catch (err) {
      console.error("Failed to download transcript:", err);
    }
  }, [customerId, transcriptDownload]);

  // -- Handlers: row --------------------------------------------------------
  const toggleCheckbox = useCallback((id: string) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = () => {
    setCheckedIds((prev) =>
      prev.size === interactions.length
        ? new Set()
        : new Set(interactions.map((i) => i.interactionId)),
    );
  };

  // -- Handlers: filter change -----------------------------------------------
  const handleFiltersChange = useCallback(
    (newFilters: SearchFilters) => {
      setFilters(newFilters);
      setPage(0);
      setSearchParams(filtersToParams(newFilters), { replace: true });
    },
    [setSearchParams],
  );

  const handleFiltersReplace = useCallback((next: SearchFilters) => {
    setFilters(next);
    setPage(0);
    setSearchParams(filtersToParams(next));
  }, [setSearchParams]);

  // -- Handlers: search / pagination ----------------------------------------
  const handleRowsPerPageChange = (rows: number) => {
    setPageSize(rows);
    setPage(0);
  };

  // -- Handlers: drag reorder -----------------------------------------------
  const handleDragStart = (index: number) => {
    dragIndexRef.current = index;
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (targetIndex: number) => {
    const sourceIndex = dragIndexRef.current;
    if (sourceIndex === null || sourceIndex === targetIndex) {
      dragIndexRef.current = null;
      setDragOverIndex(null);
      return;
    }
    setColumnOrder((prev) => {
      const next = [...prev];
      const [moved] = next.splice(sourceIndex, 1);
      next.splice(targetIndex, 0, moved);
      try {
        localStorage.setItem(COLUMN_ORDER_STORAGE_KEY, JSON.stringify(next));
      } catch {
        // ignore
      }
      return next;
    });
    dragIndexRef.current = null;
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    dragIndexRef.current = null;
    setDragOverIndex(null);
  };

  // -- Active sort info for indicators --------------------------------------
  const activeSort = sortModel[0] ?? null;

  // -- Render ---------------------------------------------------------------
  const displayPage = page + 1;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
        backgroundColor: "var(--bg-deep)",
      }}
    >
      {/* Filter bar */}
      <FilterBar
        filters={filters}
        onFiltersChange={handleFiltersChange}
        selectedCount={checkedIds.size}
        onBulkExport={(e) => setExportAnchor(e.currentTarget)}
        onBulkEdit={() => { if (canBulkEdit) setBulkEditOpen(true); }}
        customerId={customerId || ""}
        siteMap={siteMap}
      />

      {/* Saved search bar */}
      {user?.userId && customerId && (
        <SavedSearchBar
          customerId={customerId}
          userId={user.userId}
          filters={filters}
          onFiltersChange={handleFiltersReplace}
        />
      )}

      {/* Content row: results pane + optional detail pane */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          flex: 1,
          overflow: "hidden",
        }}
      >
        {/* Results pane */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            overflow: "hidden",
            minWidth: 0,
          }}
        >
          {/* Toolbar: Columns button */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: "12px",
              py: "6px",
              borderBottom: "1px solid var(--border-subtle)",
              backgroundColor: "var(--bg-surface)",
              flexShrink: 0,
            }}
          >
            <Box
              component="button"
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => setColumnsMenuAnchor(e.currentTarget)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                px: "10px",
                py: "4px",
                borderRadius: "6px",
                border: "1px solid var(--border-mid)",
                bgcolor: "var(--bg-raised)",
                color: "var(--text-secondary)",
                fontSize: "0.75rem",
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "inherit",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                "&:hover": { bgcolor: "var(--bg-hover)", color: "var(--text-primary)" },
              }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} style={{ width: 14, height: 14 }}>
                <rect x="3" y="3" width="7" height="7" rx="1" />
                <rect x="14" y="3" width="7" height="7" rx="1" />
                <rect x="3" y="14" width="7" height="7" rx="1" />
                <rect x="14" y="14" width="7" height="7" rx="1" />
              </svg>
              {t("search:columns")}
            </Box>

            {/* Density button */}
            <Box
              component="button"
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => setDensityMenuAnchor(e.currentTarget)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                px: "10px",
                py: "4px",
                borderRadius: "6px",
                border: "1px solid var(--border-mid)",
                bgcolor: "var(--bg-raised)",
                color: "var(--text-secondary)",
                fontSize: "0.75rem",
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "inherit",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                "&:hover": { bgcolor: "var(--bg-hover)", color: "var(--text-primary)" },
              }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} style={{ width: 14, height: 14 }}>
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
              {t("search:density")}
            </Box>
          </Box>

          {/* Density popover */}
          <Popover
            open={Boolean(densityMenuAnchor)}
            anchorEl={densityMenuAnchor}
            onClose={() => setDensityMenuAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            slotProps={{
              paper: {
                sx: {
                  backgroundColor: "var(--bg-raised)",
                  border: "1px solid var(--border-mid)",
                  borderRadius: "8px",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
                  p: 0.5,
                  minWidth: 140,
                  color: "var(--text-primary)",
                },
              },
            }}
          >
            {(["compact", "standard", "comfortable"] as Density[]).map((d) => {
              const densityActiveBg = "var(--accent-dim)";
              const densityActiveColor = "var(--accent)";
              const densityInactiveColor = "var(--text-secondary)";
              return (
              <Box
                key={d}
                component="button"
                onClick={() => handleDensityChange(d)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  width: "100%",
                  px: "12px",
                  py: "8px",
                  border: "none",
                  borderRadius: "6px",
                  backgroundColor: d === density ? densityActiveBg : "transparent",
                  color: d === density ? densityActiveColor : densityInactiveColor,
                  fontSize: "0.8125rem",
                  fontWeight: d === density ? 600 : 400,
                  cursor: "pointer",
                  fontFamily: "inherit",
                  textTransform: "capitalize",
                  "&:hover": {
                    backgroundColor: "var(--bg-hover)",
                  },
                }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={d === "compact" ? 2.5 : 2} style={{ width: 14, height: 14 }}>
                  {d === "compact" && (
                    <>
                      <line x1="3" y1="5" x2="21" y2="5" />
                      <line x1="3" y1="10" x2="21" y2="10" />
                      <line x1="3" y1="15" x2="21" y2="15" />
                      <line x1="3" y1="20" x2="21" y2="20" />
                    </>
                  )}
                  {d === "standard" && (
                    <>
                      <line x1="3" y1="6" x2="21" y2="6" />
                      <line x1="3" y1="12" x2="21" y2="12" />
                      <line x1="3" y1="18" x2="21" y2="18" />
                    </>
                  )}
                  {d === "comfortable" && (
                    <>
                      <line x1="3" y1="8" x2="21" y2="8" />
                      <line x1="3" y1="16" x2="21" y2="16" />
                    </>
                  )}
                </svg>
                {t(`search:${d}`)}
              </Box>
              );
            })}
          </Popover>

          {/* Columns visibility popover */}
          <Popover
            open={Boolean(columnsMenuAnchor)}
            anchorEl={columnsMenuAnchor}
            onClose={() => { setColumnsMenuAnchor(null); setColumnSearch(""); }}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            slotProps={{
              paper: {
                sx: {
                  backgroundColor: "var(--bg-raised)",
                  border: "1px solid var(--border-mid)",
                  borderRadius: "8px",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
                  minWidth: 240,
                  color: "var(--text-primary)",
                  display: "flex",
                  flexDirection: "column",
                  maxHeight: 440,
                },
              },
            }}
          >
            {/* Search input */}
            <Box sx={{ p: "10px 10px 6px", flexShrink: 0 }}>
              <Box
                component="input"
                type="text"
                placeholder={t("search:searchColumns")}
                value={columnSearch}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setColumnSearch(e.target.value)}
                sx={{
                  width: "100%",
                  px: "10px",
                  py: "7px",
                  fontSize: "0.8125rem",
                  fontFamily: "inherit",
                  color: "var(--text-primary)",
                  backgroundColor: "var(--bg-surface)",
                  border: "1px solid var(--border-mid)",
                  borderRadius: "6px",
                  outline: "none",
                  boxSizing: "border-box",
                  "&::placeholder": { color: "var(--text-tertiary)" },
                  "&:focus": { borderColor: "var(--accent)" },
                }}
              />
            </Box>

            {/* Column checkboxes */}
            <Box sx={{ overflowY: "auto", flex: 1, px: 1, pb: 0.5 }}>
              {orderedColumns
                .filter((col) => {
                  if (!columnSearch.trim()) return true;
                  return getLabel(col.field).toLowerCase().includes(columnSearch.toLowerCase());
                })
                .map((col) => (
                <FormControlLabel
                  key={col.field}
                  control={
                    <Checkbox
                      checked={!hiddenColumns.has(col.field)}
                      onChange={() => toggleColumnVisibility(col.field)}
                      size="small"
                      sx={{
                        color: "var(--text-muted)",
                        "&.Mui-checked": { color: "var(--accent)" },
                      }}
                    />
                  }
                  label={getLabel(col.field)}
                  sx={{
                    display: "flex",
                    mx: 0,
                    py: 0.25,
                    "& .MuiFormControlLabel-label": {
                      fontSize: "0.8125rem",
                      color: "var(--text-secondary)",
                    },
                  }}
                />
              ))}
            </Box>

            {/* Footer: Show/Hide All + Reset */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderTop: "1px solid var(--border-subtle)",
                px: "10px",
                py: "8px",
                flexShrink: 0,
              }}
            >
              <Box
                component="button"
                onClick={() => {
                  const allVisible = hiddenColumns.size === 0;
                  if (allVisible) {
                    // Hide all
                    setHiddenColumns(new Set(COLUMN_DEFS.map((c) => c.field)));
                  } else {
                    // Show all
                    setHiddenColumns(new Set());
                  }
                  try { localStorage.setItem(COLUMN_VISIBILITY_STORAGE_KEY, JSON.stringify(allVisible ? COLUMN_DEFS.map((c) => c.field) : [])); } catch {}
                }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  background: "none",
                  border: "none",
                  color: "var(--text-secondary)",
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  cursor: "pointer",
                  fontFamily: "inherit",
                  "&:hover": { color: "var(--text-primary)" },
                }}
              >
                <Checkbox
                  checked={hiddenColumns.size === 0}
                  indeterminate={hiddenColumns.size > 0 && hiddenColumns.size < COLUMN_DEFS.length}
                  size="small"
                  sx={{
                    p: 0,
                    color: "var(--text-muted)",
                    "&.Mui-checked, &.MuiCheckbox-indeterminate": { color: "var(--accent)" },
                  }}
                />
                {t("search:showHideAll")}
              </Box>
              <Box
                component="button"
                onClick={() => {
                  setHiddenColumns(new Set(DEFAULT_HIDDEN_COLUMNS));
                  setColumnWidths({});
                  try { localStorage.setItem(COLUMN_VISIBILITY_STORAGE_KEY, JSON.stringify([...DEFAULT_HIDDEN_COLUMNS])); } catch {}
                  try { localStorage.removeItem(COLUMN_WIDTHS_STORAGE_KEY); } catch {}
                }}
                sx={{
                  background: "none",
                  border: "none",
                  color: "var(--text-muted)",
                  fontSize: "0.75rem",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  "&:hover": { color: "var(--text-primary)" },
                }}
              >
                {t("common:reset")}
              </Box>
            </Box>
          </Popover>

          {/* Scrollable table area */}
          <Box ref={scrollContainerRef} sx={{ flex: 1, overflowY: "auto", overflowX: "auto" }}>
            {/* NOSONAR */}
            {isLoading ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "200px",
                  gap: "12px",
                  color: "var(--text-secondary)",
                }}
              >
                <CircularProgress size={20} />
                <span>{t("common:loading")}</span>
              </Box>
            ) : interactions.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "200px",
                  color: "var(--text-tertiary)",
                  fontSize: "0.875rem",
                }}
              >
                {t("search:noResultsFound")}
              </Box>
            ) : (
              <Box
                component="table"
                ref={tableRef}
                className="data-table"
                style={columnWidthVars as React.CSSProperties}
                sx={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "0.8125rem",
                  "& thead th": {
                    position: "sticky",
                    top: 0,
                    zIndex: 2,
                    backgroundColor: "var(--bg-surface)",
                    borderBottom: "1px solid var(--border-subtle)",
                    px: ds.cellPx,
                    py: ds.cellPy,
                    textAlign: "left",
                    fontWeight: 600,
                    fontSize: ds.headerFontSize,
                    color: "var(--text-tertiary)",
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    whiteSpace: "nowrap",
                  },
                  "& .pinned-col": {
                    zIndex: "5 !important",
                  },
                  "& tbody tr": {
                    borderBottom: "1px solid var(--border-subtle)",
                    "&:hover": {
                      backgroundColor: "var(--bg-hover)",
                    },
                  },
                  "& td": {
                    px: ds.cellPx,
                    py: ds.cellPy,
                    verticalAlign: "middle",
                    whiteSpace: "nowrap",
                    fontSize: ds.fontSize,
                  },
                }}
              >
                <thead>
                  <tr>
                    {/* Checkbox header — pinned */}
                    <Box component="th" className="pinned-col" sx={{ width: 40, minWidth: 40, textAlign: "center !important" as any, verticalAlign: "middle !important" as any, position: "sticky", left: 0, backgroundColor: "var(--bg-surface)" }}>
                      <Box
                        component="input"
                        type="checkbox"
                        checked={interactions.length > 0 && checkedIds.size === interactions.length}
                        onChange={toggleSelectAll}
                        sx={{
                          accentColor: "var(--accent)",
                          width: 14,
                          height: 14,
                          cursor: "pointer",
                          margin: "0 auto",
                          display: "block",
                        }}
                      />
                    </Box>

                    {/* Type header — pinned */}
                    <Box component="th" className="pinned-col" sx={{ width: 70, minWidth: 70, position: "sticky", left: 40, backgroundColor: "var(--bg-surface)" }}>
                      {t("grid:type")}
                    </Box>

                    {/* Actions header — pinned */}
                    <Box
                      component="th"
                      sx={{
                        width: 180,
                        minWidth: 180,
                        textAlign: "center",
                        position: "sticky",
                        left: 110,
                        backgroundColor: "var(--bg-surface)",
                        borderRight: "1px solid var(--border-subtle)",
                      }}
                      className="pinned-col"
                    >
                      {t("common:actions")}
                    </Box>

                    {/* Data columns */}
                    {visibleColumns.map((col, idx) => {
                      const isSorted = activeSort?.field === col.field;
                      return (
                        <Box
                          component="th"
                          key={col.field}
                          draggable
                          onDragStart={() => handleDragStart(idx)}
                          onDragOver={(e: React.DragEvent) => handleDragOver(e, idx)}
                          onDragLeave={handleDragLeave}
                          onDrop={() => handleDrop(idx)}
                          onDragEnd={handleDragEnd}
                          onClick={() => handleSort(col.field)}
                          sx={{
                            cursor: "pointer",
                            userSelect: "none",
                            position: "relative",
                            opacity: dragIndexRef.current === idx ? 0.4 : 1,
                            borderLeft:
                              dragOverIndex === idx
                                ? "2px solid var(--accent)"
                                : "2px solid transparent",
                            "&:hover": {
                              color: "var(--text-secondary) !important",
                            },
                            width: `var(--col-w-${col.field}, auto)`,
                            minWidth: `var(--col-w-${col.field}, auto)`,
                            maxWidth: `var(--col-w-${col.field}, none)`,
                            overflow: "hidden",
                            "&:hover .resize-handle": { opacity: 1 },
                          }}
                        >
                          <Box
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "4px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {getLabel(col.field)}
                            {isSorted && (
                              <Box
                                component="span"
                                sx={{
                                  fontSize: "0.625rem",
                                  color: "var(--accent)",
                                  lineHeight: 1,
                                  flexShrink: 0,
                                }}
                              >
                                {activeSort.sort === "asc" ? "\u25B2" : "\u25BC"}
                              </Box>
                            )}
                          </Box>
                          {/* Resize handle */}
                          <Box
                            className="resize-handle"
                            draggable={false}
                            onMouseDown={(e: React.MouseEvent) => handleResizeStart(e, col.field)}
                            onDoubleClick={(e: React.MouseEvent) => {
                              e.stopPropagation();
                              setColumnWidths(prev => {
                                const next = { ...prev };
                                delete next[col.field];
                                try { localStorage.setItem(COLUMN_WIDTHS_STORAGE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
                                return next;
                              });
                            }}
                            onClick={(e: React.MouseEvent) => e.stopPropagation()}
                            sx={{
                              position: "absolute",
                              right: -2,
                              top: 0,
                              bottom: 0,
                              width: "5px",
                              cursor: "col-resize",
                              opacity: 0,
                              zIndex: 1,
                              transition: "opacity 150ms ease",
                              "&:hover, &:active": {
                                opacity: 1,
                                backgroundColor: "var(--accent)",
                              },
                            }}
                          />
                        </Box>
                      );
                    })}

                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const virtualItems = rowVirtualizer.getVirtualItems();
                    return (
                      <>
                        {/* Top spacer — pushes visible rows to correct scroll position */}
                        {virtualItems.length > 0 && (
                          <tr style={{ height: virtualItems[0].start }} />
                        )}

                        {virtualItems.map((virtualRow) => {
                          const row = interactions[virtualRow.index];
                          return (
                            <TableRow
                              key={row.interactionId}
                              row={row}
                              isChecked={checkedIds.has(row.interactionId)}
                              isViewing={viewingId === row.interactionId}
                              initialTab={initialTab}
                              canBulkEdit={canBulkEdit}
                              canExport={canExport}
                              canReplay={canReplay}
                              visibleColumns={visibleColumns}
                              columnCtx={columnCtx}
                              typeColumnDef={typeColumnDef}
                              onToggleCheckbox={toggleCheckbox}
                              onEdit={handleEdit}
                              onDownloadFile={handleDownloadFile}
                              onDownloadTranscript={handleDownloadTranscript}
                              onInfo={handleInfo}
                              onPlay={handlePlay}
                            />
                          );
                        })}

                        {/* Bottom spacer — maintains total scroll height */}
                        {virtualItems.length > 0 && (
                          <tr style={{
                            height: rowVirtualizer.getTotalSize() -
                              (virtualItems.at(-1)?.end ?? 0),
                          }} />
                        )}
                      </>
                    );
                  })()}
                </tbody>
              </Box>
            )}
          </Box>

          {/* Pagination */}
          <Pagination
            page={displayPage}
            totalPages={Math.max(1, totalPages)}
            rowsPerPage={pageSize}
            totalCount={totalCount}
            onPageChange={(p) => setPage(p - 1)}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        </Box>

        {/* Detail pane */}
        {viewingInteraction && customerId && (
          <DetailPane
            key={`${viewingInteraction.interactionId}-${initialTab}`}
            interactionId={viewingInteraction.interactionId}
            customerId={customerId}
            interactionType={viewingInteraction.interactionType}
            onClose={() => setViewingId(null)}
            initialTab={initialTab}
          />
        )}

        {/* Single edit drawer */}
        {customerId && (
          <EditInteractionDrawer
            open={editingInteraction !== null}
            onClose={() => setEditingInteraction(null)}
            interaction={editingInteraction}
            customerId={customerId}
          />
        )}

        {/* Bulk edit drawer */}
        {customerId && (
          <BulkEditDrawer
            open={bulkEditOpen}
            onClose={() => setBulkEditOpen(false)}
            interactions={interactions.filter((i) => checkedIds.has(i.interactionId))}
            customerId={customerId}
          />
        )}
      </Box>

      {/* Bulk export popover */}
      {customerId && (
        <BulkExportPopover
          open={Boolean(exportAnchor)}
          anchorEl={exportAnchor}
          onClose={() => setExportAnchor(null)}
          customerId={customerId}
          selectedIds={Array.from(checkedIds)}
        />
      )}
    </Box>
  );
}
